import {Injectable} from '@angular/core';
import {StorageService} from '@services/storage/storage.service';
import {MachineListUserConfigApiService} from '@services/dashboard/machine-list-user-config-api.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {catchError} from 'rxjs/operators';
import {firstValueFrom, of} from 'rxjs';
import {
  createDefaultUserConfig,
  DEFAULT_CATEGORY_STATE,
  MACHINE_LIST_ITEMS_PER_PAGE_OPTIONS,
  MACHINE_LIST_VIEW_USER_CONFIG_KEY,
  MachineListCategoryPreferences,
  MachineListViewUserConfig,
} from '@models/dashboard/machine-list/machine-list-view-user-config';

export interface IUserConfigStore {
  load(): Promise<MachineListViewUserConfig>;
  save(config: MachineListViewUserConfig): Promise<void>;
}

@Injectable({
  providedIn: 'root',
})
export class MachineListUserConfigService implements IUserConfigStore {
  private readonly storage: StorageService;
  private readonly storageKey: Promise<string>;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SAVE_DEBOUNCE_MS = 300;

  constructor(
    storage: StorageService,
    private readonly apiService: MachineListUserConfigApiService,
    private readonly firebaseAuthenticationService: FirebaseAuthenticationService,
  ) {
    this.storage = storage;
    this.storageKey = this.resolveStorageKey();
  }

  private async resolveStorageKey(): Promise<string> {
    try {
      const user = await firstValueFrom(this.firebaseAuthenticationService.getLoggedUserDetails());
      return user?.firebaseUid
        ? `${MACHINE_LIST_VIEW_USER_CONFIG_KEY}:${user.firebaseUid}`
        : MACHINE_LIST_VIEW_USER_CONFIG_KEY;
    } catch (error) {
      console.error('Error resolving user for machine list configuration', error);
      return MACHINE_LIST_VIEW_USER_CONFIG_KEY;
    }
  }

  public async load(): Promise<MachineListViewUserConfig> {
    const storageKey = await this.storageKey;
    try {
      const raw = await this.storage.readValueFromIndexedDb<string>(storageKey);
      if (raw !== undefined && raw !== null) {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      console.error('Error loading machine list user config from indexedDb', error);
    }

    const backendConfig = await firstValueFrom(this.apiService.get().pipe(
      catchError((error) => {
        if (error.status !== 404 && error.status !== 204) {
          console.error('Error loading machine list user config from backend', error);
        }
        return of(null);
      }),
    ));
    const config = this.mergeWithDefaults(backendConfig);
    try {
      await this.storage.writeValueToIndexedDb(storageKey, config);
    } catch (error) {
      console.error('Error caching machine list user config in indexedDb', error);
    }
    return config;
  }

  public save(config: MachineListViewUserConfig): Promise<void> {
    const safeConfig = this.mergeWithDefaults(config);
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    return new Promise<void>((resolve) => {
      this.saveTimer = setTimeout(() => {
        this.persist(safeConfig).then(resolve);
      }, this.SAVE_DEBOUNCE_MS);
    });
  }

  public saveNow(config: MachineListViewUserConfig): Promise<void> {
    const safeConfig = this.mergeWithDefaults(config);
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    return this.persist(safeConfig);
  }

  private async persist(config: MachineListViewUserConfig): Promise<void> {
    const storageKey = await this.storageKey;
    try {
      await this.storage.writeValueToIndexedDb(storageKey, config);
    } catch (error) {
      console.error('Error saving machine list user config to indexedDb', error);
    }

    try {
      await firstValueFrom(this.apiService.save(config));
    } catch (error) {
      console.error('Error saving machine list user config to backend', error);
    }
  }

  private mergeWithDefaults(partial: Partial<MachineListViewUserConfig> | null | undefined): MachineListViewUserConfig {
    const base = createDefaultUserConfig();
    if (!partial) {
      return base;
    }

    const categories = Object.entries(partial.categories ?? {}).reduce<Record<string, MachineListCategoryPreferences>>(
      (result, [categoryName, category]) => {
        const itemsPerPage = category?.itemsPerPage ?? DEFAULT_CATEGORY_STATE.itemsPerPage;
        result[categoryName] = {
          itemsPerPage: MACHINE_LIST_ITEMS_PER_PAGE_OPTIONS.includes(itemsPerPage)
            ? itemsPerPage
            : DEFAULT_CATEGORY_STATE.itemsPerPage,
          visibleColumns: Array.isArray(category?.visibleColumns) ? [...category.visibleColumns] : [],
        };
        return result;
      },
      {},
    );

    return {
      activeCategory: partial.activeCategory ?? base.activeCategory,
      categories,
    };
  }
}
